import type {
  ClubMessageData,
  ClubMessageDataRequest,
  ClubMessageDataResponse,
  HgClubMessageDataReply
} from '@ps/web-shared-api-types/v1-preview/SwHomeGames';
import { useDispatch, useSelector } from 'react-redux';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import type { ErrorBody } from '@swf/core/errors/RequestError';
import useRequest from '@swf/core/hooks/useRequest';
import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';
import { handleServerError, handleServerResponse } from '@swf/poker';

import ENDPOINTS from '../../../../constants/endpoints';
import { ModalRoutes } from '../../../../types/common';

interface FormFields {
  post?: string | undefined;
  id?: number;
  obsolete?: boolean;
  pinnedPos?: number;
  parentId?: number;
}

interface Props extends Pick<ClubMessageData, 'roleFlags'> {
  clubId: string;
  setLoading: (loading: boolean) => void;
}

const useClubWallRequest = ({ clubId, setLoading, roleFlags }: Props) => {
  const request = useRequest();
  const userId = useSelector(select.account.getUserId);
  const { modal } = useDispatch<Dispatch>();

  const handleSubmit = ({
    id,
    post = '',
    obsolete = false,
    pinnedPos = 0,
    parentId
  }: FormFields) => {
    setLoading(true);
    const clubMessageData: ClubMessageData = {
      type: 'HG_MESSAGE_TYPE_WALL',
      content: post,
      clubId: Number(clubId),
      userId,
      obsolete,
      id,
      roleFlags,
      flags: 'REGULAR',
      pinnedPos
    };

    if (parentId) {
      clubMessageData.parentId = parentId;
    }

    try {
      request({
        service: ENDPOINTS.clubMessageRequest,
        http: true,
        body: {
          clubMessageData: {
            clubMessageData
          }
        } as ClubMessageDataRequest
      })
        .pipe(catchError((err) => of(err)))
        .subscribe({
          next: ({ body }) => {
            const { error } = handleServerResponse<ClubMessageDataResponse, HgClubMessageDataReply>(
              {
                response: body,
                accessor: 'clubMessageDataReply'
              }
            );

            if (error) {
              modal.enqueue({
                path: ModalRoutes.notification,
                locationState: { text: error }
              });
            }

            setLoading(false);
          }
        });
    } catch (e) {
      handleServerError(e as ErrorBody);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  return { handleSubmit };
};

export default useClubWallRequest;
